import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useRef, useEffect, useMemo, } from 'react';
import { observer } from 'mobx-react-lite';
import { Box, Button, Typography } from '@mui/material';
import { useConversationStore } from '../ConversationStoreProvider';
import { replaceStylesheetGlobals } from '../portal/theme/portalTheme';
import ReferenceLink from './ReferenceLink';
const DualSearchTopSection = ({ topSectionMaxHeightPx = 400, 
// Legacy prop (backward compatibility)
seeMoreButtonLabel, 
// New semantic props
seeMoreLabel = 'See more results', seeLessLabel = 'See less', referenceFormatting, openWebPagesInNewTab = false, onSeeMoreClick, }) => {
    // Use new semantic props with fallback to legacy prop
    const finalSeeMoreLabel = seeMoreLabel ?? seeMoreButtonLabel ?? 'See more results';
    const finalSeeLessLabel = seeLessLabel ?? 'See less';
    const store = useConversationStore();
    const conversation = store?.currentConversation;
    const stylesheet = store?.stylesheet || {};
    const [isExpanded, setIsExpanded] = useState(false);
    const [showSeeMoreButton, setShowSeeMoreButton] = useState(false);
    const contentRef = useRef(null);
    const containerRef = useRef(null);
    // Get references from the last assistant message
    // Top Section should only show web references (links), not products (products go to Right Pane)
    const references = useMemo(() => {
        if (!conversation) {
            return [];
        }
        const allReferences = [];
        const seenKeys = new Set();
        // Get references from all assistant messages, prioritizing the last one
        const assistantMessages = conversation.messages.filter((m) => m.role === 'assistant');
        // Process messages in reverse order to get most recent first
        [...assistantMessages].reverse().forEach((message) => {
            // Use carouselItems (same as ReferencesSidebar) instead of itemReferenceKeys
            if (message.carouselItems && Array.isArray(message.carouselItems)) {
                message.carouselItems.forEach((item) => {
                    const { key, title, description, url, image, type, } = item;
                    // Only include non-product references (web references) that have a URL
                    // Products go to Right Pane, not Top Section
                    if (key && !seenKeys.has(key) && url && type !== 'product' && title) {
                        allReferences.push({
                            key,
                            title,
                            description,
                            url,
                            image,
                            type,
                        });
                        seenKeys.add(key);
                    }
                });
            }
        });
        return allReferences;
    }, [conversation]);
    // Check if content exceeds max height
    useEffect(() => {
        if (!contentRef.current || !containerRef.current) {
            setShowSeeMoreButton(false);
            return undefined;
        }
        const checkHeight = () => {
            if (!contentRef.current) {
                setShowSeeMoreButton(false);
                return;
            }
            const element = contentRef.current;
            // Use scrollHeight to get total content height (not just visible)
            const { scrollHeight, clientHeight, offsetHeight } = element;
            const contentHeight = Math.max(scrollHeight, clientHeight, offsetHeight);
            const maxHeight = topSectionMaxHeightPx;
            const shouldShow = contentHeight > maxHeight;
            setShowSeeMoreButton(shouldShow);
        };
        // Initial check
        checkHeight();
        // Set up ResizeObserver to detect content changes
        const resizeObserver = new ResizeObserver(() => {
            checkHeight();
        });
        resizeObserver.observe(contentRef.current);
        // Set up MutationObserver to detect DOM changes
        const mutationObserver = new MutationObserver(() => {
            checkHeight();
        });
        mutationObserver.observe(contentRef.current, {
            childList: true,
            subtree: true,
            attributes: true,
        });
        // Also check after delays to catch late DOM updates
        const timeout1 = setTimeout(checkHeight, 200);
        const timeout2 = setTimeout(checkHeight, 500);
        const timeout3 = setTimeout(checkHeight, 1000);
        return () => {
            resizeObserver.disconnect();
            mutationObserver.disconnect();
            clearTimeout(timeout1);
            clearTimeout(timeout2);
            clearTimeout(timeout3);
        };
    }, [references, topSectionMaxHeightPx, isExpanded]);
    const handleSeeMoreClick = () => {
        setIsExpanded(true);
        // Call parent callback to show native results
        if (onSeeMoreClick) {
            onSeeMoreClick();
        }
    };
    const handleSeeLessClick = () => {
        setIsExpanded(false);
    };
    if (!references || references.length === 0) {
        return null;
    }
    const containerStyle = {
        width: '100%',
        marginBottom: '24px',
        padding: '16px',
        backgroundColor: replaceStylesheetGlobals(stylesheet, 'color_background') || '#ffffff',
        border: referenceFormatting?.outline
            ? `${referenceFormatting.outlineWeight || 1}px ${referenceFormatting.outlineType || 'solid'} ${referenceFormatting.outlineColor || '#e5e7eb'}`
            : '1px solid #e5e7eb',
        borderRadius: '8px',
    };
    const contentStyle = {
        maxHeight: isExpanded ? 'none' : `${topSectionMaxHeightPx}px`,
        overflow: isExpanded ? 'visible' : 'hidden',
        transition: 'max-height 0.3s ease-in-out',
    };
    return (_jsxs(Box, { ref: containerRef, sx: containerStyle, children: [_jsx(Box, { sx: {
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '12px',
                }, children: _jsx(Typography, { variant: "h6", sx: {
                        fontWeight: 600,
                        color: replaceStylesheetGlobals(stylesheet, 'color_font') || '#1f2937',
                    }, children: "AI Search Results" }) }), _jsx(Box, { ref: contentRef, sx: contentStyle, children: references
                    .filter((ref) => ref.url) // Only render references with URL
                    .map((reference, index) => (_jsx(ReferenceLink, { reference: {
                        ...reference,
                        url: reference.url, // We filtered above, so url is guaranteed
                    }, referenceFormatting: referenceFormatting, openWebPagesInNewTab: openWebPagesInNewTab }, reference.key || index))) }), showSeeMoreButton && !isExpanded && (_jsx(Box, { sx: { marginTop: '12px', textAlign: 'center' }, children: _jsx(Button, { variant: "outlined", onClick: handleSeeMoreClick, sx: {
                        color: replaceStylesheetGlobals(stylesheet, 'color_primary') || '#3b82f6',
                        borderColor: replaceStylesheetGlobals(stylesheet, 'color_primary') || '#3b82f6',
                        '&:hover': {
                            borderColor: replaceStylesheetGlobals(stylesheet, 'color_primary') || '#3b82f6',
                            backgroundColor: replaceStylesheetGlobals(stylesheet, 'color_primary') || '#3b82f6',
                            color: '#ffffff',
                        },
                    }, children: finalSeeMoreLabel }) })), isExpanded && showSeeMoreButton && (_jsx(Box, { sx: { marginTop: '12px', textAlign: 'center' }, children: _jsx(Button, { variant: "outlined", onClick: handleSeeLessClick, sx: {
                        color: replaceStylesheetGlobals(stylesheet, 'color_primary') || '#3b82f6',
                        borderColor: replaceStylesheetGlobals(stylesheet, 'color_primary') || '#3b82f6',
                        '&:hover': {
                            borderColor: replaceStylesheetGlobals(stylesheet, 'color_primary') || '#3b82f6',
                            backgroundColor: replaceStylesheetGlobals(stylesheet, 'color_primary') || '#3b82f6',
                            color: '#ffffff',
                        },
                    }, children: finalSeeLessLabel }) }))] }));
};
DualSearchTopSection.defaultProps = {
    topSectionMaxHeightPx: 400,
    seeMoreButtonLabel: 'See more results',
    referenceFormatting: undefined,
    openWebPagesInNewTab: false,
    onSeeMoreClick: undefined,
};
export default observer(DualSearchTopSection);
//# sourceMappingURL=DualSearchTopSection.js.map